import React, { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";
import { CheckCircle2, Copy, Download, Eye, EyeOff, Loader2, ShieldCheck, X } from "lucide-react";
import { apiUrl } from "../api";
import PasskeySecuritySection from "./PasskeySecuritySection";

const TEXT = {
  en: {
    title: "Two-Factor Authentication",
    enabled: "Two-factor authentication is active.",
    disabled: "Two-factor authentication is not active.",
    enable: "Enable two-factor authentication",
    loading: "Loading MFA status...",
    beginFailed: "Failed to start MFA enrollment.",
    confirmFailed: "Invalid code. Try again.",
    statusFailed: "Failed to load MFA status.",
    step1: "Open Microsoft Authenticator and scan the QR code.",
    compatible: "You can also use another authenticator app that supports TOTP codes.",
    manualPrompt: "Cannot scan the code?",
    showKey: "Show setup key",
    hideKey: "Hide setup key",
    copyKey: "Copy setup key",
    keyWarning: "The setup key is sensitive. Do not share it.",
    codeLabel: "6-digit code",
    confirm: "Confirm enrollment",
    confirming: "Confirming...",
    cancel: "Cancel",
    recoveryTitle: "Save your recovery codes",
    recoveryIntro: "Recovery codes let you sign in if you lose access to your authenticator app.",
    recoveryOnce: "Each code can be used once. After closing this screen, these codes cannot be shown again.",
    copyAll: "Copy all",
    download: "Download .txt",
    savedCheck: "I saved these recovery codes in a safe place.",
    finish: "Done",
    copied: "Copied.",
    account: "Account",
    generated: "Generated",
  },
  he: {
    title: "אימות דו-שלבי",
    enabled: "אימות דו-שלבי פעיל.",
    disabled: "אימות דו-שלבי אינו פעיל.",
    enable: "הפעל אימות דו-שלבי",
    loading: "טוען סטטוס אימות...",
    beginFailed: "לא ניתן להתחיל הפעלת אימות דו-שלבי.",
    confirmFailed: "קוד שגוי. נסה שוב.",
    statusFailed: "שגיאה בטעינת סטטוס האימות.",
    step1: "פתח את Microsoft Authenticator וסרוק את קוד ה-QR.",
    compatible: "ניתן להשתמש גם באפליקציית אימות אחרת התומכת בקודי TOTP.",
    manualPrompt: "לא ניתן לסרוק את הקוד?",
    showKey: "הצג מפתח הגדרה",
    hideKey: "הסתר מפתח הגדרה",
    copyKey: "העתק מפתח",
    keyWarning: "מפתח ההגדרה רגיש. אין לשתף אותו.",
    codeLabel: "קוד בן 6 ספרות",
    confirm: "אשר הפעלה",
    confirming: "מאשר...",
    cancel: "ביטול",
    recoveryTitle: "שמור את קודי השחזור",
    recoveryIntro: "קודי השחזור מאפשרים להתחבר לחשבון אם אין לך גישה לאפליקציית האימות.",
    recoveryOnce: "כל קוד ניתן לשימוש פעם אחת בלבד. לאחר סגירת המסך לא ניתן יהיה להציג אותם שוב.",
    copyAll: "העתק הכל",
    download: "הורד קובץ",
    savedCheck: "שמרתי את קודי השחזור במקום בטוח.",
    finish: "סיום",
    copied: "הועתק.",
    account: "חשבון",
    generated: "נוצר",
  },
};

const normalizeLanguage = (language) => (language === "en" ? "en" : "he");

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("userToken") || ""}`,
});

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
};

const MfaSecuritySection = ({ language = "he" }) => {
  const uiLanguage = normalizeLanguage(language);
  const t = TEXT[uiLanguage];
  const isRtl = uiLanguage === "he";
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [codesSaved, setCodesSaved] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const response = await fetch(apiUrl("/auth?action=mfa_status"), { headers: authHeaders() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || t.statusFailed);
      setStatus(data?.mfa || {});
    } catch (error) {
      setFeedback({ type: "error", text: error.message || t.statusFailed });
    } finally {
      setLoading(false);
    }
  }, [t.statusFailed]);

  useEffect(() => {
    loadStatus();
    return () => {
      setEnrollment(null);
      setQrDataUrl("");
      setRecoveryCodes([]);
    };
  }, [loadStatus]);

  const beginEnrollment = async () => {
    setWorking(true);
    setFeedback(null);
    setRecoveryCodes([]);
    try {
      const response = await fetch(apiUrl("/auth?action=mfa_begin_enrollment"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || t.beginFailed);
      const uri = data?.otpauth_uri || "";
      const qr = await QRCode.toDataURL(uri, { width: 220, margin: 2, errorCorrectionLevel: "M" });
      setEnrollment({
        secret: data?.secret || "",
        otpauthUri: uri,
        account: data?.account || readUser().username || "",
      });
      setQrDataUrl(qr);
      setShowSecret(false);
      setCode("");
    } catch (error) {
      setFeedback({ type: "error", text: error.message || t.beginFailed });
    } finally {
      setWorking(false);
    }
  };

  const confirmEnrollment = async (event) => {
    event.preventDefault();
    const cleanCode = code.replace(/\D/g, "").slice(0, 6);
    if (cleanCode.length !== 6) {
      setFeedback({ type: "error", text: t.confirmFailed });
      return;
    }
    setWorking(true);
    setFeedback(null);
    try {
      const response = await fetch(apiUrl("/auth?action=mfa_confirm_enrollment"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ code: cleanCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || t.confirmFailed);
      setRecoveryCodes(Array.isArray(data?.recovery_codes) ? data.recovery_codes : []);
      setEnrollment(null);
      setQrDataUrl("");
      setCode("");
      setCodesSaved(false);
      setStatus({ enabled: true, enrolled: true });
    } catch (error) {
      setFeedback({ type: "error", text: error.message || t.confirmFailed });
    } finally {
      setWorking(false);
    }
  };

  const cancelEnrollment = () => {
    setEnrollment(null);
    setQrDataUrl("");
    setCode("");
    setShowSecret(false);
    setFeedback(null);
  };

  const recoveryText = () => {
    const user = readUser();
    return [
      "TagWeigh Recovery Codes",
      `${t.account}: ${user.username || enrollment?.account || ""}`,
      `${t.generated}: ${new Date().toLocaleString()}`,
      "",
      ...recoveryCodes,
      "",
      "Each code can be used once.",
      "Store these codes securely.",
    ].join("\n");
  };

  const copyText = async (text) => {
    await navigator.clipboard?.writeText(text);
    setFeedback({ type: "success", text: t.copied });
  };

  const downloadCodes = () => {
    const blob = new Blob([recoveryText()], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "tagweigh-recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="text-sm font-black text-slate-500">{t.loading}</div>;
  }

  return (
    <div className="space-y-5" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-slate-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <ShieldCheck className={status?.enabled ? "text-emerald-600" : "text-slate-400"} size={24} />
          <div>
            <div className="font-black text-slate-950">{t.title}</div>
            <div className="text-sm font-bold text-slate-500">{status?.enabled ? t.enabled : t.disabled}</div>
          </div>
        </div>
        {!status?.enabled && !enrollment && recoveryCodes.length === 0 && (
          <button
            type="button"
            onClick={beginEnrollment}
            disabled={working}
            className="rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white transition hover:bg-slate-800 disabled:opacity-60"
          >
            {working ? <Loader2 size={18} className="animate-spin" /> : t.enable}
          </button>
        )}
      </div>

      {feedback && <FeedbackMessage message={feedback} />}

      {enrollment && (
        <form onSubmit={confirmEnrollment} className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
          <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
            <div className="flex flex-col items-center justify-center rounded-3xl bg-slate-50 p-5">
              {qrDataUrl && <img src={qrDataUrl} alt="TOTP QR code" className="h-[220px] w-[220px]" />}
            </div>
            <div className="min-w-0">
              <p className="font-black text-slate-950">{t.step1}</p>
              <p className="mt-2 text-sm font-bold leading-6 text-slate-500">{t.compatible}</p>
              <div className="mt-4 rounded-2xl bg-amber-50 p-4 text-sm font-bold text-amber-900">
                {t.keyWarning}
              </div>
              <div className="mt-4">
                <button type="button" onClick={() => setShowSecret((v) => !v)} className="inline-flex items-center gap-2 text-sm font-black text-blue-700">
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showSecret ? t.hideKey : t.showKey}
                </button>
                {showSecret && (
                  <div className="mt-3 rounded-2xl bg-slate-100 p-3">
                    <code className="block break-all text-sm font-black text-slate-900">{enrollment.secret}</code>
                    <button type="button" onClick={() => copyText(enrollment.secret)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-black text-slate-700">
                      <Copy size={14} />
                      {t.copyKey}
                    </button>
                  </div>
                )}
              </div>
              <label className="mt-5 block">
                <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.codeLabel}</span>
                <input
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-xl font-black tracking-[0.25em] outline-none focus:border-blue-400"
                />
              </label>
              <div className="mt-4 flex flex-wrap gap-3">
                <button type="submit" disabled={working} className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white disabled:opacity-60">
                  {working ? t.confirming : t.confirm}
                </button>
                <button type="button" onClick={cancelEnrollment} className="rounded-2xl bg-slate-100 px-5 py-3 text-sm font-black text-slate-700">
                  {t.cancel}
                </button>
              </div>
            </div>
          </div>
        </form>
      )}

      {recoveryCodes.length > 0 && (
        <div className="rounded-3xl border border-emerald-100 bg-emerald-50 p-5">
          <h4 className="text-lg font-black text-emerald-950">{t.recoveryTitle}</h4>
          <p className="mt-2 text-sm font-bold leading-6 text-emerald-900">{t.recoveryIntro}</p>
          <p className="mt-1 text-sm font-bold leading-6 text-emerald-900">{t.recoveryOnce}</p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {recoveryCodes.map((item) => (
              <code key={item} className="break-all rounded-xl bg-white px-3 py-2 text-sm font-black text-slate-900">
                {item}
              </code>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            <button type="button" onClick={() => copyText(recoveryText())} className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-black text-slate-800">
              <Copy size={16} />
              {t.copyAll}
            </button>
            <button type="button" onClick={downloadCodes} className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-black text-slate-800">
              <Download size={16} />
              {t.download}
            </button>
          </div>
          <label className="mt-4 flex cursor-pointer items-center gap-3 text-sm font-black text-emerald-950">
            <input type="checkbox" checked={codesSaved} onChange={(event) => setCodesSaved(event.target.checked)} className="h-5 w-5 accent-emerald-600" />
            {t.savedCheck}
          </label>
          <button
            type="button"
            disabled={!codesSaved}
            onClick={() => {
              setRecoveryCodes([]);
              setFeedback({ type: "success", text: t.enabled });
              loadStatus();
            }}
            className="mt-4 rounded-2xl bg-emerald-700 px-5 py-3 text-sm font-black text-white disabled:opacity-50"
          >
            {t.finish}
          </button>
        </div>
      )}

      {status?.enabled && recoveryCodes.length === 0 && !enrollment && (
        <PasskeySecuritySection language={uiLanguage} />
      )}
    </div>
  );
};

const FeedbackMessage = ({ message }) => {
  const isError = message?.type === "error";
  return (
    <div className={`flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black ${isError ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
      {isError ? <X size={17} /> : <CheckCircle2 size={17} />}
      {message.text}
    </div>
  );
};

export default MfaSecuritySection;
